/**
 * Equipment store — visible gear on the local character.
 *
 * R232q5h ARCH.1: This store is the SINGLE SOURCE OF TRUTH for equipped
 * gear on the client. All UI (Inventory.tsx, CharacterPanel.tsx, the
 * rig's setEquipment) reads from here. Writes go through `equip()` /
 * `unequip()` / `applyServerEquipped()`.
 *
 * Persistence:
 *   1. localStorage cache for instant boot.
 *   2. Server overrides on character load (applyServerEquipped).
 *   3. Per-mutation server sync handled at call site.
 */

import { create } from 'zustand';
import type { Slot } from '../data/equipment.js';

const STORAGE_KEY = 'aiwari-equipped-v1';

interface EquipStore {
  equipped: Partial<Record<Slot, string>>;
  setEquipped: (next: Partial<Record<Slot, string>>) => void;
  equip: (slot: Slot, itemId: string) => void;
  unequip: (slot: Slot) => void;
  /** R232q5h ARCH.1: server-driven snapshot. Overwrites local without
   *  triggering another save loop. Call this when DB hydrate finishes. */
  applyServerEquipped: (m: Partial<Record<Slot, string>>) => void;
}

function load(): Partial<Record<Slot, string>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as Partial<Record<Slot, string>> : {};
  } catch { return {}; }
}

function save(m: Partial<Record<Slot, string>>) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(m)); } catch {}
}

export const useEquip = create<EquipStore>((set, get) => ({
  equipped: load(),
  setEquipped: (next) => { save(next); set({ equipped: next }); },
  equip: (slot, itemId) => {
    const next = { ...get().equipped, [slot]: itemId };
    save(next);
    set({ equipped: next });
  },
  unequip: (slot) => {
    const next = { ...get().equipped };
    delete next[slot];
    save(next);
    set({ equipped: next });
  },
  applyServerEquipped: (m) => {
    save(m);
    set({ equipped: { ...m } });
  },
}));
